import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDMY } from "@/lib/format";
import { History, ChevronDown, ChevronUp, Mail, MailX, CheckCheck, Eye } from "lucide-react";

export function RosterSentLog({ start, end }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["roster-sent", start, end],
    queryFn: async () => (await api.get(`/roster/sent?start=${start}&end=${end}`)).data,
    enabled: !!start && !!end,
    refetchInterval: 60000,
  });
  const items = data?.items || [];
  if (items.length === 0) return null;
  return (
    <div className="rounded-md border bg-card text-xs" data-testid="roster-sent-log">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-left font-semibold hover:bg-secondary/40" data-testid="roster-sent-toggle">
        <History className="h-3.5 w-3.5 text-muted-foreground" />
        {t("Sent roster changes")} <span className="rounded-full bg-secondary px-1.5 text-[10px]" data-testid="roster-sent-count">{items.length}</span>
        <span className="ml-1 font-normal text-muted-foreground">{fmtDMY(start)} – {fmtDMY(end)}</span>
        {open ? <ChevronUp className="ml-auto h-3.5 w-3.5" /> : <ChevronDown className="ml-auto h-3.5 w-3.5" />}
      </button>
      {open && (
        <ul className="divide-y border-t" data-testid="roster-sent-items">
          {items.map((it) => (
            <li key={it.id} className="px-3 py-2" data-testid={`roster-sent-${it.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-semibold">{it.cleaner_name}</span>
                <span className="text-muted-foreground">· {t("sent by {{name}}", { name: it.sent_by })} · {new Date(it.sent_at).toLocaleString()}</span>
                {it.emailed
                  ? <span className="inline-flex items-center gap-0.5 text-emerald-700" title={t("Email sent")}><Mail className="h-3 w-3" /> {t("Email sent")}</span>
                  : <span className="inline-flex items-center gap-0.5 text-muted-foreground" title={t("In-app message only")}><MailX className="h-3 w-3" /> {t("In-app message only")}</span>}
                <span className={`inline-flex items-center gap-0.5 ${it.seen ? "text-emerald-700" : "text-amber-700"}`} data-testid={`roster-sent-seen-${it.id}`}>
                  {it.seen ? <><CheckCheck className="h-3 w-3" /> {t("Seen by cleaner")}</> : <><Eye className="h-3 w-3" /> {t("Not yet seen")}</>}
                </span>
              </div>
              <ul className="mt-0.5 space-y-0.5 text-[11px] text-muted-foreground">
                {it.lines.map((ln, i) => <li key={i}>{ln}</li>)}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
